import React, { FormEvent, Fragment, ChangeEvent, MouseEvent } from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import ReactModal from "react-modal";

import { apiGet, apiDelete, V2, ISurvey, V1 } from "../../Api";
import { formatDateLocal, formatDateTimeLocal, formatName, formatId, formatPhone } from "../../Utilities";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import HasRight from "../HasRight";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";
import NoteBox from "../NoteBox";
import withAuth from "../withAuth";
import withTitle, { IViewTitleProps } from "../withTitle";
import withProps from "../withProps";

import "../../blocks.css";
import "../../button.css";
import "../../table.css";
import "../viewer.css";

import UOGLogo from "../../images/UOG-Logo-2-row-trim.png";
import TabControl from "../TabControl";

interface IQuestionnaireViewProps extends RouteComponentProps<any>, IViewTitleProps, IAuthProps {
    mode?: "digital";
    root?: "print";
}

interface IQuestionnaireViewState {
    isSubmitting?: boolean | string;
    survey: ISurvey | null

    isLoading: boolean | string;

    isDeletionModalOpen: boolean;
    deletionModalLoading: boolean | string;

    isModalOpen: boolean;
    modalLoading: boolean | string;

    isOnCallDialogOpen: boolean;
    onCallDialogLoading: boolean | string;
    onCallGroup: string;

    consultNote: string;

    isContactModalOpen: boolean;
    contactModalLoading: boolean | string;
    contactNote: string;
    contactFiles: string[];
    proofRequired: boolean;

    isCallbackDialogOpen: boolean;
    callbackGroup: string;
    contactStatus: string;
    contactStartDate: string;
    contactStartTime: string;
    contactEndDate: string;
    contactEndTime: string;
}

class QuestionnaireView extends React.Component<IQuestionnaireViewProps, IQuestionnaireViewState> {
    private contactNoteRef: React.RefObject<NoteBox> = React.createRef();

    constructor(props: Readonly<IQuestionnaireViewProps>) {
        super(props);

        this.state = {
            survey: null,

            isLoading: true,

            deletionModalLoading: false,
            isDeletionModalOpen: false,

            isModalOpen: false,
            modalLoading: false,

            isOnCallDialogOpen: false,
            onCallDialogLoading: "",
            onCallGroup: "",

            consultNote: "",

            isContactModalOpen: false,
            contactModalLoading: "",
            contactNote: "",
            contactFiles: [],
            proofRequired: true,

            isCallbackDialogOpen: false,
            callbackGroup: "",
            contactStatus: "",
            contactStartDate: "",
            contactStartTime: "",
            contactEndDate: "",
            contactEndTime: "",
        };

        this.createTask = this.createTask.bind(this);
        this.printPage = this.printPage.bind(this);
        this.deleteItem = this.deleteItem.bind(this);
        this.realDeleteItem = this.realDeleteItem.bind(this);
        this.closeDeletionModal = this.closeDeletionModal.bind(this);
        this.sendToConsultation = this.sendToConsultation.bind(this);
        this.realSendToConsultation = this.realSendToConsultation.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.dialogInputChanged = this.dialogInputChanged.bind(this);

        this.showContactDialog = this.showContactDialog.bind(this);
        this.closeContactModal = this.closeContactModal.bind(this);
        this.setContactStatus = this.setContactStatus.bind(this);
        this.handleContactNote = this.handleContactNote.bind(this);
        this.handleContactNoteChange = this.handleContactNoteChange.bind(this);
        this.submitContactNote = this.submitContactNote.bind(this);
        this.sendToOnCall = this.sendToOnCall.bind(this);
        this.realSendToOnCall = this.realSendToOnCall.bind(this);
        this.closeOnCallDialog = this.closeOnCallDialog.bind(this);

        this.openCallbackDialog = this.openCallbackDialog.bind(this);
        this.closeCallbackDialog = this.closeCallbackDialog.bind(this);
        this.submitContactDialog = this.submitContactDialog.bind(this);
    }

    public async componentDidMount() {
        this.loadData();
    }

    public render() {
        if (typeof this.state.isLoading === "string") {
            return (
                <div className="viewer-root">
                    <div className="error-box">{this.state.isLoading}</div>
                </div>
            );
        }

        if (!this.state.survey || this.state.isLoading) {
            return (
                <div className="spinner-full-root">
                    <div className="spinner lds-dual-ring-big" />
                </div>
            );
        }

        const survey = this.state.survey;

        return (
            <div className="viewer-root viewer-print-page with-actionbar">
                <ViewToolbar>
                    <ToolbarMenuItem right="survey:write" condition={this.props.mode !== "digital"}>
                        <Link to={`${this.props.location.pathname}/edit`} className="icon-pencil" title="Make Correction" />
                    </ToolbarMenuItem>
                    <ToolbarMenuItem>
                        <button type="button" className="icon-print" title="Print" onClick={this.printPage} />
                    </ToolbarMenuItem>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="survey:delete">
                            <button type="button" onClick={this.deleteItem}><i className="icon-trash" /><span>Delete {this.props.mode !== "digital" ? "questionnaire" : "digital survey"}</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="survey:contact" condition={!this.props.authContext.serverType && this.state.survey.sex !== "M" && this.props.mode !== "digital"}>
                            <button type="button" onClick={this.showContactDialog}><i className="icon-clock-o" /><span>Contacted</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem allRights={["survey:contact", "oreach:sc"]} condition={this.state.survey.sex === "F"}>
                            <button type="button" onClick={() => this.openCallbackDialog(WellKnownGroups.SurgicalConsultant)}><i className="icon-phone" /><span style={{ textAlign: "left" }}>Contacted <br /><small>(Surgical Consultant)</small></span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem allRights={["survey:contact", "oreach:tm"]} condition={this.state.survey.sex === "F"}>
                            <button type="button" onClick={() => this.openCallbackDialog(WellKnownGroups.PatientCareRep)}><i className="icon-phone" /><span style={{ textAlign: "left" }}>Contacted <br /><small>(Patient Care Rep)</small></span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem allRights={["survey:write"]} condition={survey.contactAllowed}>
                            <button type="button" onClick={this.setDoNotContact}><i className="icon-ban" /><span>Contact Not Allowed</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <ReactModal
                    isOpen={this.state.isDeletionModalOpen} onRequestClose={this.closeDeletionModal}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-deletion-portal"
                    bodyOpenClassName="modal-deletion-open"
                    contentLabel="Confirm Delete">
                    <form className="input-form" onSubmit={this.realDeleteItem}>
                        <section className="info-block">
                            <header>
                                <h3>Confirm Delete</h3>
                            </header>

                            <span>Are you sure you want to delete this {this.props.mode !== "digital" ? "questionnaire" : "digital survey"}?</span>
                            <p>
                                {formatName(this.state.survey!.name)}<br />
                                {this.props.mode !== "digital" && this.state.survey!.source !== 3 ? <small>{this.state.survey!.healthFair.name}</small> : null}
                            </p>
                            {
                                this.state.deletionModalLoading === true
                                    ? <div className="spinner lds-dual-ring" />
                                    : (typeof this.state.deletionModalLoading) === "string"
                                        ? <span className="error-text">{this.state.deletionModalLoading}</span>
                                        : ""
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <button type="submit" className="button red push-right">Yes, Delete</button>
                                <button type="button" className="button" onClick={this.closeDeletionModal}>No</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                {this.state.isModalOpen
                    ? <ReactModal
                        isOpen={this.state.isModalOpen} onRequestClose={this.closeModal}
                        overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                        bodyOpenClassName="modal-open"
                        contentLabel="Send to Consultation dialog">
                        <form className="input-form" onSubmit={this.realSendToConsultation}>
                            <section className="info-block">
                                <header>
                                    <h3>Send to Consultation</h3>
                                </header>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-consult-note">Note</label>
                                        <textarea id="frm-consult-note" className="input-textarea-medium" name="consultNote" value={this.state.consultNote} onChange={this.dialogInputChanged} autoComplete="off_please_chrome" />
                                    </div>
                                </section>
                                <br />
                                {
                                    this.state.modalLoading === true
                                        ? <Fragment><div className="spinner lds-dual-ring" /><br /></Fragment>
                                        : typeof this.state.modalLoading === "string"
                                            ? <Fragment><span className="error-text">{this.state.modalLoading}</span><br /></Fragment>
                                            : ""
                                }
                                <div className="action-panel-dialog">
                                    <button type="submit" className="button green push-right" disabled={!this.state.consultNote}>Send</button>
                                    <button type="button" className="button" onClick={this.closeModal}>Close</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null}
                {this.state.isContactModalOpen
                    ? <ReactModal
                        isOpen={this.state.isContactModalOpen} onRequestClose={this.closeContactModal}
                        overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-billing-portal"
                        bodyOpenClassName="modal-billing-open"
                        contentLabel="Contacted dialog">
                        <form className="input-form">
                            <section className="info-block">
                                <header>
                                    <h3>Contacted patient</h3>
                                </header>

                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-contact-note">Proof of contact <small>(note is optional)</small></label>
                                        <NoteBox ref={this.contactNoteRef} defaultNoteOpen={true} allowUpload={true} requireUpload={this.state.proofRequired && !this.props.authContext.hasRight("survey:contact:nproof") && !this.props.authContext.hasRight("survey:contact:tproof")} uploadCompress="image" hideLoading={true} stayOpenAfterSubmit={true} mode="single" onNewNote={this.handleContactNote} onNewNoteChange={this.handleContactNoteChange} />
                                        {this.props.authContext.hasRight("survey:contact:nproof")
                                            ? <small>You are not required to upload proof to change status</small>
                                            : this.props.authContext.hasRight("survey:contact:tproof")
                                                ? <small><b>Note:</b> You must at least enter a note, except for <b>Do Not Contact</b></small>
                                                : <small><b>Note:</b> You must upload at least one proof of contact, except for <b>Do Not Contact</b></small>
                                        }
                                    </div>
                                </section>
                                {
                                    this.state.contactModalLoading === true
                                        ? <div className="spinner lds-dual-ring" />
                                        : (typeof this.state.contactModalLoading) === "string"
                                            ? <span className="error-text">{this.state.contactModalLoading}</span>
                                            : ""
                                }
                                &nbsp;
                                <div className="action-panel-dialog mobile-text-smaller">
                                    <div className="spring fill-space" />
                                    <button type="button" className="button green" onClick={() => this.setContactStatus("gotappointment")}>Reached<br /><small>Make Appointment</small></button>
                                    <button type="button" className="button yellow" onClick={() => this.setContactStatus("reached")}>Reached<br /><small>Didn't want anything</small></button>
                                    <button type="button" className="button blue" onClick={() => this.setContactStatus("reachedcontactlater")}>Reached<br /><small>Wants contact later</small></button>
                                    <button type="button" className="button red" onClick={() => this.setContactStatus("notreached")}>Did not reach</button>
                                    <button type="button" className="button purple" onClick={() => this.setContactStatus("surgmedcontact")}>Surg med<br />Contacted</button>
                                    <button type="button" className="button orange" onClick={() => this.setContactStatus("surgmedappt")}>Surg med<br />Appointment</button>
                                    <button type="button" className="button btn-outline" onClick={() => this.setContactStatus("donotcontact")}>Do Not<br />Contact</button>
                                    <button type="button" className="button" onClick={this.closeContactModal}>Close</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null}
                {this.state.isOnCallDialogOpen
                    ? <ReactModal
                        isOpen={this.state.isOnCallDialogOpen} onRequestClose={this.closeOnCallDialog}
                        overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-billing-portal"
                        bodyOpenClassName="modal-billing-open"
                        contentLabel="Contacted dialog">
                        <form className="input-form">
                            <section className="info-block">
                                <header>
                                    <h3>Send to On-Call {this.groupToTitle(this.state.onCallGroup)}</h3>
                                </header>

                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="on-call-note">Note <small>(optional)</small></label>
                                        <textarea id="on-call-note" name="contactNote" value={this.state.contactNote} onChange={this.dialogInputChanged} />
                                    </div>
                                </section>
                                &nbsp;
                                <div className="action-panel-dialog">
                                    <div className="spring" />
                                    <button type="button" className="button" onClick={this.realSendToOnCall}>Submit</button>
                                    <button type="button" className="button" onClick={this.closeOnCallDialog}>Close</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null
                }
                {this.state.isCallbackDialogOpen
                    ? <ReactModal isOpen={this.state.isCallbackDialogOpen} onRequestClose={this.closeCallbackDialog}
                        overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-portal"
                        bodyOpenClassName="modal-open" contentLabel="Contact dialog">
                        <form className="input-form">
                            <section className="info-block">
                                <header>
                                    <h3>Contacted</h3>
                                </header>

                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="contact-result">Contact result</label>
                                        <select id="contact-result" name="contactStatus" value={this.state.contactStatus} onChange={this.dialogInputChanged}>
                                            <option value="">Select an option...</option>
                                            <option value="reachedmakeappointment">Reached patient, schedule an appointment</option>
                                            <option value="reacheddidntwant">Reached patient, did not want anything</option>
                                            <option value="calllater">Reached patient, contact again later</option>
                                            <option value="donotcontact">Do not contact again</option>
                                        </select>
                                    </div>
                                </section>
                                &nbsp;
                                <section>
                                    <div className="input-form-group">
                                        <label>Note {!this.props.authContext.hasRight("oreach:c:note:opt") ? <small>(required)</small> : <small>(optional)</small>}</label>
                                        <NoteBox ref={this.contactNoteRef} defaultNoteOpen={true} allowUpload={true} requireUpload={false} uploadCompress="image" hideLoading={true} stayOpenAfterSubmit={true} mode="single" onNewNote={this.handleContactNote} onNewNoteChange={this.handleContactNoteChange} />
                                    </div>
                                </section>
                                {this.renderContactDialogInnerBody()}
                                {
                                    this.state.modalLoading === true
                                        ? <div className="spinner lds-dual-ring" />
                                        : typeof this.state.modalLoading === "string"
                                            ? <div className="error-text"><br />{this.state.modalLoading}</div>
                                            : null
                                }
                            &nbsp;
                            <div className="action-panel-dialog">
                                    <span className="spring" />
                                    <button type="button" className="button green" onClick={this.submitContactDialog} disabled={this.isContactDialogButtonDisabled()}>Submit</button>
                                    <button type="button" className="button" onClick={this.closeCallbackDialog}>Close</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null}
                <QuestionnaireViewInner
                    survey={survey}
                    mode={this.props.mode}
                    displayContactInfo={this.props.authContext.hasRight("survey:contact") && !this.props.authContext.serverType}
                    submitContactNote={this.submitContactNote}
                    userDisplayName={this.props.authContext.user.displayName}
                />
                &nbsp;
                {typeof this.state.isSubmitting === "string" ? <span className="error-text">{this.state.isSubmitting}</span> : ""}
                <div className="action-panel-dialog">
                    {
                        this.props.authContext.hasRight("survey:refer") && this.state.survey.appointmentMode !== 2
                            ? <>
                                <button type="button" className="button text-with-icon" onClick={() => this.sendToOnCall(WellKnownGroups.PatientCareRep)} disabled={this.state.isSubmitting === true}>
                                    <i className="icon-mail-forward" /><span>Send to Patient Care Rep.</span>
                                </button>
                                <button type="button" className="button text-with-icon" onClick={() => this.sendToOnCall(WellKnownGroups.SurgicalConsultant)} disabled={this.state.isSubmitting === true}>
                                    <i className="icon-mail-forward" /><span>Send to Surgery Consultation</span>
                                </button>
                            </>
                            : null
                    }
                    {
                        this.state.survey.appointmentMode === 2
                            ? <button className="button push-right text-with-icon" onClick={this.sendToConsultation}> <i className="icon-share" /><span>Send to Pellet Consultation</span></button>
                            : this.state.survey.hasAppointments
                                ? <HasRight right="appt:read"><Link className="button push-right text-with-icon" to={{ pathname: `/surveys/${this.props.match.params.id}/appointments` }}> <i className="icon-calendar" /><span>View Appointment(s)</span></Link></HasRight>
                                : this.props.mode !== "digital"
                                    ? <HasRight right="appt:create"><Link className="button push-right text-with-icon" to={{ pathname: "/appointments/create", state: { survey: this.state.survey } }}> <i className="icon-plus" /><span>New Appointment</span></Link></HasRight>
                                    : <span className="push-right" />
                    }
                    <button type="button" className="button text-with-icon" onClick={this.createTask} disabled={this.state.isSubmitting === true}>
                        <i className="icon-th-list" /><span>Create Task</span>
                    </button>
                </div>
            </div >
        );
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true,
            })

            const mode = this.props.authContext.hasRight("survey:contact") ? "full_contact" : "full";
            const json = await apiGet<ISurvey>(this.props.mode === "digital"
                ? `survey/digital/${this.props.match.params.id}?questions=${"full"}`
                : `survey/${this.props.match.params.id}?questions=${mode}`);

            if (json.contactNotes) {
                json.contactNotes
                    .sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp))
                    .forEach((a: any) => a.author = formatName(a.employee.name));
            }

            // this.props.viewTitleContext.changeTitle(`Questionnaire - ${json.name.first} ${json.name.last}`);

            this.setState({
                isLoading: false,
                survey: json,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message,
                survey: null,
            });
        }
    }

    private printPage() {
        if (!this.state.survey) {
            return;
        }

        const oldTitle = document.title;
        document.title = `Questionnaire (${formatName(this.state.survey.name)})`;
        window.print();
        document.title = oldTitle;
    }

    private async createTask() {
        if (!this.state.survey) {
            return;
        }

        let subject = `Contact Later - ${formatName(this.state.survey.name)}`;
        if (this.state.survey.healthFair) {
            subject += ` (${this.state.survey.healthFair.name})`;
        }

        this.props.history.push("/tasks/new", {
            attachments: [{
                displayText: formatName(this.state.survey.name),
                id: this.state.survey.id,
                type: "survey",
            }],
            navigationSteps: 2,
            subject
        });
    }

    private deleteItem() {
        this.setState({
            isDeletionModalOpen: true
        });
    }

    private closeDeletionModal() {
        this.setState({
            isDeletionModalOpen: false
        });
    }

    private async realDeleteItem(e?: FormEvent) {
        if (e) {
            e.preventDefault();
        }

        try {
            this.setState({
                deletionModalLoading: true
            });

            await apiDelete(this.props.mode === "digital"
                ? `survey/digital/${this.state.survey!.id}`
                : `survey/${this.state.survey!.id}`);

            this.setState({
                deletionModalLoading: false
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                deletionModalLoading: e.message || e
            });
        }
    }

    private sendToConsultation() {
        this.setState({
            isModalOpen: true
        });
    }

    private async realSendToConsultation(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                modalLoading: true
            });

            const data = {
                surveyId: this.state.survey!.id,
                note: this.state.consultNote
            };

            await V2.post(`consult`, data);

            this.setState({
                modalLoading: false,
                isModalOpen: false,
            }, () => this.props.history.goBack());
        }
        catch (e) {
            this.setState({
                modalLoading: e.message || e || "Could not submit data"
            });
        }
    }

    private closeModal() {
        this.setState({
            isModalOpen: false
        });
    }

    private dialogInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLTextAreaElement>) {
        const name = e.currentTarget.name;
        const value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            [name]: value
        } as any);
    }

    private showContactDialog() {
        this.setState({
            isContactModalOpen: true
        });
    }

    private closeContactModal() {
        this.setState({
            isContactModalOpen: false
        });
    }

    private async setContactStatus(status: string) {
        if (!this.contactNoteRef.current || !this.state.survey) {
            return;
        }

        this.setState({
            contactModalLoading: true,
            proofRequired: status !== "donotcontact",
        }, async () => {
            if (!this.contactNoteRef.current || !this.state.survey) {
                return;
            }

            // Using a callback to make sure proofRequired is set properly
            try {
                let statusNumber = 0;
                switch (status) {
                    case "notreached": statusNumber = 1; break;
                    case "reached": statusNumber = 2; break;
                    case "gotappointment": statusNumber = 3; break;
                    case "donotcontact": statusNumber = 4; break;
                    case "reachedcontactlater": statusNumber = 5; break;
                    case "surgmedcontact": statusNumber = 6; break;
                    case "surgmedappt": statusNumber = 7; break;
                    default: throw new Error("Unknown status code");
                }

                // If we have file IDs already, don't attempt to re-upload
                if (this.state.contactFiles.length === 0) {
                    // Process note and upload files
                    if (!await this.contactNoteRef.current.submit()) {
                        this.setState({
                            contactModalLoading: false,
                        });
                        return;
                    }
                }

                if (this.props.authContext.hasRight("survey:contact:tproof") && !this.props.authContext.hasRight("survey:contact:nproof") && !this.state.contactNote.trim()) {
                    // Note is required
                    this.setState({
                        contactModalLoading: "You must enter a note",
                    });
                    return;
                }

                const data = {
                    status: statusNumber,
                    note: this.state.contactNote,
                    attachments: this.state.contactFiles
                };

                await V2.post(`survey/${this.state.survey.id}/contact`, data);

                this.setState({
                    contactModalLoading: false,
                    isContactModalOpen: false,
                    contactNote: "",
                    contactFiles: [],
                }, () => {
                    if (statusNumber === 3) {
                        // Send to create appointment
                        this.props.history.push(`/appointments/create`, { survey: this.state.survey });
                    }
                    else {
                        this.props.history.goBack();
                    }
                });
            }
            catch (e) {
                this.setState({
                    contactModalLoading: e.message || e || "Could not set status"
                });
            }
        });
    }

    private async handleContactNote(body: string, files?: string[]) {
        this.setState({
            contactNote: body,
            contactFiles: files || []
        });
    }

    private handleContactNoteChange(body: string) {
        this.setState({
            contactNote: body
        });
    }

    private async submitContactNote(body: string, files?: string[]) {
        const note = {
            body,
            files
        };

        const noteResult = await V2.post<any>(`survey/${this.state.survey!.id}/notes/contact`, note);
        noteResult.author = formatName(noteResult.employee.name);

        this.setState(s => {
            if (!s.survey!.contactNotes) {
                s.survey!.contactNotes = [];
            }

            s.survey!.contactNotes!.push(noteResult);

            return {
                survey: s.survey,
            }
        });
    }

    private sendToOnCall(group: string) {
        this.setState({
            isOnCallDialogOpen: true,
            onCallGroup: group
        });
    }

    private closeOnCallDialog() {
        this.setState({
            isOnCallDialogOpen: false,
        });
    }

    private async realSendToOnCall() {
        try {
            this.setState({
                onCallDialogLoading: true
            });

            const data = {
                groupId: this.state.onCallGroup,
                note: this.state.contactNote
            };

            await V2.post(`survey/${this.state.survey!.id}/on-call`, data);

            this.setState({
                onCallDialogLoading: false
            }, () => this.props.history.goBack());
        }
        catch (e) {
            this.setState({
                onCallDialogLoading: e.message || e.error || "Could not submit "
            });
        }
    }

    private groupToTitle(group: string) {
        switch (group) {
            case WellKnownGroups.SurgicalConsultant: return "Surgical Consultant";
            case WellKnownGroups.PatientCareRep: return "Patient Care Rep.";
            case WellKnownGroups.AppointmentSpecialist: return "Appointment Specialist.";
            default: throw new Error("Unknown group " + group);
        }
    }

    private openCallbackDialog(group: string) {
        this.setState({
            isCallbackDialogOpen: true,
            callbackGroup: group
        });
    }

    private closeCallbackDialog() {
        this.setState({
            isCallbackDialogOpen: false,
            modalLoading: false,

            contactStatus: "",
            contactStartDate: "",
            contactStartTime: "",
            contactEndDate: "",
            contactEndTime: "",
        });
    }

    private renderContactDialogInnerBody() {
        switch (this.state.contactStatus) {
            case "reachedmakeappointment":
                return <section>
                    <span>You will be sent to the appointment creation page after submission.</span>
                </section>;
            case "calllater":
                return <>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="contact-start-date">Range Start Date</label>
                            <input type="date" id="contact-start-date" name="contactStartDate" value={this.state.contactStartDate} onChange={this.dialogInputChanged} />
                        </div>
                        <div className="input-form-group">
                            <label htmlFor="contact-start-time">Range Start Time</label>
                            <input type="time" id="contact-start-time" name="contactStartTime" value={this.state.contactStartTime} onChange={this.dialogInputChanged} />
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="contact-end-date">Range End Date</label>
                            <input type="date" id="contact-end-date" name="contactEndDate" value={this.state.contactEndDate} onChange={this.dialogInputChanged} />
                        </div>
                        <div className="input-form-group">
                            <label htmlFor="contact-end-time">Range End Time</label>
                            <input type="time" id="contact-end-time" name="contactEndTime" value={this.state.contactEndTime} onChange={this.dialogInputChanged} />
                        </div>
                    </section>
                </>;
            default:
                return null;
        }
    }

    private isContactDialogButtonDisabled() {
        let base = !this.props.authContext.hasRight("oreach:c:note:opt") && !this.state.contactNote?.trim();
        switch (this.state.contactStatus) {
            case "calllater": return base || !this.state.contactStartDate || !this.state.contactStartTime;
            default: return base || !this.state.contactStatus;
        }
    }

    private async submitContactDialog() {
        try {
            this.setState({
                modalLoading: true
            });

            const data: any = {
                status: this.state.contactStatus
            };

            if (this.state.contactFiles.length === 0) {
                // Process note and upload files
                if (await this.contactNoteRef.current?.submit() === false) {
                    this.setState({
                        modalLoading: false,
                    });
                    return;
                }
            }

            if (this.state.contactNote) {
                data.note = this.state.contactNote;
                data.attachments = this.state.contactFiles;
            }

            if (this.state.contactStatus === "calllater") {
                data.callbackStart = `${this.state.contactStartDate}T${this.state.contactStartTime}`;
                data.callbackEnd = this.state.contactEndDate && this.state.contactEndTime
                    ? `${this.state.contactEndDate}T${this.state.contactEndTime}`
                    : undefined;
            }

            await V2.put<any>(`survey/outreach/group/${this.state.callbackGroup}/${this.state.survey!.id}/contact`, data);

            this.setState({
                modalLoading: false,
            });

            switch (data.status) {
                case "reachedmakeappointment":
                    // Send to appointment creation
                    this.closeCallbackDialog();
                    this.props.history.replace("/appointments/create", { survey: this.state.survey, allowNoProof: true });
                    break;
                default:
                    // Go back
                    this.closeCallbackDialog();
                    this.props.history.goBack();
                    break;
            }
        }
        catch (e) {
            this.setState({
                modalLoading: e.message || e.error || "Could not save contact status"
            });
        }
    }

    private async setDoNotContact() {
        try {
            const data = {
                contactAllowed: false
            };

            await V1.patch(`survey/${this.state.survey?.id}`, data);

            this.loadData();
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not set contact status"
            });
        }
    }
}

export type QuestionnaireInnerEvent = "outreach_text_nav";

export interface IQuestionnaireViewInnerProps {
    survey: any;
    mode?: "digital";
    displayContactInfo: boolean;
    userDisplayName: string;
    canViewMessages?: boolean;

    submitContactNote: (body: string, files?: string[]) => PromiseLike<void>;
    onInnerEvent?: (type: QuestionnaireInnerEvent, args: any) => boolean | undefined;
}

export class QuestionnaireViewInner extends React.Component<IQuestionnaireViewInnerProps> {
    constructor(props: Readonly<IQuestionnaireViewInnerProps>) {
        super(props);

        this.outreachNoteRenderExtra = this.outreachNoteRenderExtra.bind(this);
    }

    public render() {
        const survey = this.props.survey;
        const isLead = survey.source === 3;

        const answerTable = <table className="section-table nohover q-table">
            <thead>
                <tr>
                    <th>Question</th>
                    <th className="col-smallest">Answer</th>
                </tr>
            </thead>
            <tbody>
                {
                    survey.answers && survey.answers.length > 0
                        ? survey.answers.map((a: any) => (
                            <tr key={a.id}>
                                <td className="wrap">{a.displayText}</td>
                                <td className="print-hide col-center">{a.answerText === "Yes" ? <b>{a.answerText}</b> : <span className="text-light">{a.answerText}</span>}</td>
                                <td className="print-only">
                                    <label style={{ display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}><input type="radio" name={a.id} checked={a.answerText === "Yes"} readOnly={true} />Yes</label>
                                    <label style={{ display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}><input type="radio" name={a.id} checked={a.answerText === "No"} readOnly={true} />No</label>
                                </td>
                            </tr>
                        ))
                        : <tr>
                            <td>No questions answered</td>
                            <td />
                        </tr>
                }
            </tbody>
        </table>;

        const outreachSection = <section className="info-block print-hide">
            <header>
                <h3>Outreach</h3>
            </header>
            <p>{this.renderOutreachInfo(survey.contactInfo, survey.callbackInfo?.[0])}</p>
            <NoteBox displayName={this.props.userDisplayName} notes={survey.contactNotes} onNewNote={this.props.submitContactNote} allowUpload={true} uploadCompress="image" hideControls={!this.props.displayContactInfo} noteHeaderExtraRender={this.outreachNoteRenderExtra} noteAuthorGenerator={this.noteAuthorGenerator} />
        </section>;

        return <div>
            <header className="page-header print-only">
                <img src={UOGLogo} alt="UOG Logo" />
                <h3 className="page-header-title">Digital Health Fair Questionnaire</h3>
                <h3 className="page-header-number">{formatId(this.props.survey.id || "")}</h3>
            </header>
            <div className="column-root">
                <section className="info-block">
                    <header>
                        <h3>Contact Information</h3>
                    </header>
                    <dl className="info-list">
                        <div className="info-list-row">
                            <div className="col-50">
                                <dt>Name</dt>
                                <dd>{formatName(survey.name)}</dd>
                            </div>
                            <div className="col-37-5">
                                <dt>Birthdate</dt>
                                <dd>{isLead && (!survey.birthdate || new Date(survey.birthdate).getFullYear() < 1800) ? <span className="text-light">See chart</span> : formatDateLocal(survey.birthdate)}</dd>
                            </div>
                            {isLead
                                ? <div className="col-12-5">
                                    <dt>Chart #</dt>
                                    <dd>{survey.outreachLeadInfo?.chartNumber || "-"}</dd>
                                </div>
                                : null}
                        </div>

                        <dt>Address</dt>
                        {isLead && !survey.address.street
                            ? <span className="text-light">See chart</span>
                            : !survey.address.street
                                ? <dd className="text-light">Not provided</dd>
                                : <>
                                    <dd>{survey.address.street}</dd>
                                    <dd>{`${survey.address.city}, ${survey.address.state} ${survey.address.zip}`}</dd>
                                </>
                        }

                        <div className="info-list-row">
                            <div className="col-50">
                                <dt>Phone number</dt>
                                {isLead && !survey.phone
                                    ? <dd><span className="text-light">See chart</span></dd>
                                    : <dd>{survey.contactAllowed ? (<a href={`tel:${survey.phone}`}>{formatPhone(survey.phone)}</a>) : formatPhone(survey.phone)}</dd>
                                }
                            </div>
                            <div className="col-37-5">
                                <dt>Can Contact?</dt>
                                <dd>{survey.contactAllowed ? "Yes" : "No"}</dd>
                            </div>
                            <div className="col-12-5">
                                <dt>Sex</dt>
                                <dd>{survey.sex || "N/A"}</dd>
                            </div>
                        </div>
                        <div>
                            <dt>Email</dt>
                            <dd>{isLead && !survey.email
                                ? <span className="text-light">See chart</span>
                                : (((survey.contactAllowed && survey.email) ? (<a href={`mailto:${survey.email}`}>{survey.email}</a>) : survey.email) || "N/A")}</dd>
                        </div>
                    </dl>
                </section>

                <section className="info-block">
                    <header>
                        <h3>Additional Information</h3>
                    </header>
                    <dl className="info-list">
                        <div className="info-list-row">
                            <div>
                                <dt>Insurance Provider</dt>
                                <dd>{((survey.insurance && survey.insurance.provider) || (isLead
                                    ? <span className="text-light">See chart</span>
                                    : "Not Provided"))}</dd>
                            </div>
                            {!isLead
                                ? <div>
                                    <dt>Employer</dt>
                                    <dd>{(survey.insurance && survey.insurance.employer) || "Not Provided"}</dd>
                                </div>
                                : null}
                        </div>

                        {
                            this.props.mode !== "digital" && !isLead
                                ? <>
                                    <dt>Health Fair</dt>
                                    <dd>{survey.healthFair.name} <Link to={`/fairs/${survey.healthFair.id}`} className="button small btn-narrow btn-inline btn-outline">View</Link></dd>
                                    <dd><small>{formatDateTimeLocal(survey.healthFair.scheduledDate)}</small></dd>

                                    <dt>Marketer(s)</dt>
                                    {survey.healthFair.members.length > 0
                                        ? <dd>{survey.healthFair.members.map((a: any) => formatName(a.name)).reduce((p: string, v: string) => `${p}, ${v}`)}</dd>
                                        : <dd>None</dd>}
                                </>
                                : null
                        }
                        {!isLead
                            ? <>
                                <dt>Questionnaire</dt>
                                <dd>{survey.surveyName || "N/A"}</dd>
                                <dd><small>Submitted: {formatDateTimeLocal(survey.timestamp)} via {this.surveyOriginToText(survey.originDevice)}</small></dd>
                            </>
                            : <>
                                <dt>Submitted</dt>
                                <dd>{formatDateTimeLocal(survey.timestamp)}</dd>
                                {!!survey.outreachLeadInfo?.creator ? <dd><small>By {formatName(survey.outreachLeadInfo?.creator?.name) || "N/A"}</small></dd> : null}
                            </>}
                        {isLead
                            ? <>
                                <dt>Note</dt>
                                <dd>
                                    <div className="wrap">
                                        {survey.outreachLeadInfo?.note || "-"}
                                    </div>
                                </dd>
                            </>
                            : null
                        }
                        {isLead && survey.outreachLeadInfo?.appointmentType
                            ? <>
                                <dt>Last Appointment</dt>
                                <dd>{survey.outreachLeadInfo?.appointmentType} on {formatDateTimeLocal(survey.outreachLeadInfo?.appointmentTimestamp)}</dd>
                                <dd>At {survey.outreachLeadInfo?.appointmentLocation} with {survey.outreachLeadInfo?.appointmentProvider}</dd>
                                {survey.outreachLeadInfo?.appointmentStatus ? <dd>Appointment was {survey.outreachLeadInfo?.appointmentStatus}</dd> : null}
                            </>
                            : null
                        }
                    </dl>
                </section>
            </div>
            &nbsp;
            {
                isLead
                    ? outreachSection
                    : this.props.displayContactInfo && this.props.mode !== "digital"
                        ? <TabControl headers={["Answers", "Outreach"]}>
                            {answerTable}
                            {outreachSection}
                        </TabControl>
                        : answerTable
            }
        </div >
    }

    private noteAuthorGenerator(note: any) {
        return note?.employee?.name && formatName(note.employee.name);
    }

    private contactStatusToString(status: number) {
        switch (status) {
            case 1: return "Did not reach";
            case 2: return "Reached, didn't want anything at this time";
            case 3: return "Reached, got appointment";
            case 4: return "Do not contact";
            case 5: return "Reached, wants to be contacted again later";
            case 6: return "Reached for surg med";
            case 7: return "Reached for surg med. Appointment created";
            default: return `Unknown (${status})`;
        }
    }

    private outreachNoteRenderExtra(note: any) {
        if (note.groupId) {
            // New contact notes
            if (!note.newStatus)
                return <span>{formatDateTimeLocal(note.timestamp)}</span>;

            return <Fragment>
                <span>{formatDateTimeLocal(note.timestamp)}</span><br />
                <span>Group: {this.outreachGroupToText(note.groupId)}</span><br />
                <span>Changed to: <b>{this.outreachStatusToText(note.newStatus)}</b> {this.props.canViewMessages && note.threadId ? <span>(<Link to={`/reminders/messages/${note.threadId}`} onClick={e => this.handleContactNavigate(e, note.threadId)}>View thread</Link>)</span> : null}</span>
                
            </Fragment>;
        }
        else {
            // Old contact notes
            if (!note.newStatus)
                return <span>{formatDateTimeLocal(note.timestamp)}</span>;

            return <Fragment>
                <span>{formatDateTimeLocal(note.timestamp)}</span><br />
                <span>Changed to: <b>{this.contactStatusToString(note.newStatus)}</b></span>
            </Fragment>;
        }
    }

    private renderOutreachInfo(contactInfo: any, callbackInfo: any) {
        if (callbackInfo) {
            return <>
                Last contacted by {this.outreachGroupToText(callbackInfo.groupId)}: <b>{this.outreachStatusToText(callbackInfo.status, callbackInfo)}</b>
            </>;
        }
        else if (contactInfo) {
            return <>
                Last contacted by marketer: <b>{this.contactStatusToString(contactInfo.status)}</b>
            </>;
        }
        else {
            return <>Last contacted: <b>Never</b></>;
        }
    }

    private outreachStatusToText(status: number, info?: any) {
        switch (status) {
            case 1: return "Did not reach";
            case 2: return "Did not reach twice";
            case 3: return info?.callbackStart
                ? `Check back after ${formatDateTimeLocal(info.callbackStart)}`
                : "Check back later";
            case 4: return info?.callbackStart
                ? `Not needed until after ${formatDateLocal(info.callbackStart)}`
                : "Not Needed";
            case 5: return "Handled Elsewhere";
            case 6: return "Did not reach three times";
            case 7: return "Text message sent";
            case 8: return "Two text messages sent";
            case 9: return "Three text messages sent";
            case 10: return "Reached, didn't want anything";
            case 11: return "Reached, made appointment";
            case 12: return "Do not contact";
            case 13: return "Made appointment afterwards";
            default: return "Unknown status " + status;
        }
    }

    private outreachGroupToText(group: string) {
        switch (group) {
            case WellKnownGroups.SurgicalConsultant: return "surgical consultant";
            case WellKnownGroups.PatientCareRep: return "patient care rep.";
            case WellKnownGroups.AppointmentSpecialist: return "appointment specialist.";
            default: return "Unknown group " + group;
        }
    }

    private surveyOriginToText(source: number) {
        switch (source) {
            case 0: return "Other device";
            case 1: return "HF Tablet";
            case 2: return "Smartphone or Computer";
            case 3: return "Online Survey";
            default: return "Other method " + source;
        }
    }

    private handleContactNavigate(e: MouseEvent<HTMLAnchorElement>, threadId: string) {
        if (this.props.onInnerEvent?.("outreach_text_nav", { threadId })) {
            e.preventDefault();
        }
    }
}

export default withTitle("Questionnaire Details", withAuth(QuestionnaireView));
export const DigitalSurveyView = withTitle("Digital Survey Details", withProps(withAuth(QuestionnaireView), { mode: "digital" }));
